/**
 * PORTED — RO-11-04 / #44589.
 * Origin: `excellent/app/_chrome/dock-bar.tsx`.
 *
 * Changes: next/navigation → @excellent/ui/router (usePathname, useNavigate,
 * useSearch). Copied into client/shell/chrome/ — never import from app/ (PRD D6).
 * Copilot chat stays behind `copilotEnabled` (default OFF via ShellData).
 */

import * as React from "react";
import { usePathname, useNavigate, useSearch } from "@excellent/ui/router";
import { useSWRConfig } from "swr";
import { Button, DropdownMenu, Icon, Input, cn, looksLikeUrl, type ContextMenuItem } from "@excellent/ui";
import { useApi, apiCall, apiKey } from "@excellent/ui/api";
import { search, type SearchHit } from "@excellent/ui/api/contracts/search";
import { chat, type ChatMessage } from "@excellent/ui/api/contracts/chat";
import { sessions } from "@excellent/ui/api/contracts/sessions";
import { auth } from "@excellent/ui/api/contracts/auth";
import {
  useSession,
  initialChatState,
  reduceChatClient,
  type ChatState,
} from "@excellent/ui/session";
import { ChatTranscript, ChatMark } from "./dock-chat-transcript";
import { useDockIntake, IntakePanel } from "./dock-bar-intake";
import { DockModelPicker } from "./dock-model-picker";

/**
 * DockBar — the bottom-docked search / command / assistant surface.
 *
 * Replaces the ⌘K command palette (`global-search.tsx`) AND surfaces the Copilot
 * chat in-place. One low-profile glass pill pinned bottom-center that expands
 * upward into:
 *   • search results (grouped, from `search.shellGet`) + the 4 palette commands,
 *   • an inline "Ask" → a real Copilot turn (streaming, tools, permissions),
 *   • a chat-history panel (new / rename / archive / resume, filterable).
 *
 * Search + commands preserve the palette's behavior (same endpoint, same
 * `excellent:create-*` events, ⌘K / "/" / open-search triggers). Chat reuses the
 * real chat contracts + the copilot renderers. SI-2-02: live turns ride the
 * session client (`useSession` + chat reducer); `excellent_chat_*` is a
 * projection (S-D8). The old `chatStream.turn` / `useStream` transport is gone.
 */

export interface DockPageSpec {
  href: string;
  label: string;
  hint?: string;
}

type Route = "search" | "chat";

const HIT_GROUP_ORDER = [
  "Issues", "Projects", "Sessions", "Plans", "Memory",
  "Documents", "Targets", "Notes", "Help",
] as const;

const GROUP_ICON: Record<string, string> = {
  Issues: "circle-check",
  Projects: "folder",
  People: "user",
  Organizations: "building-2",
  Companies: "building-2",
  Finance: "dollar-sign",
  Documents: "file-text",
  Sessions: "activity",
  Plans: "map",
  Memory: "brain",
  Targets: "target",
  Notes: "sticky-note",
  Help: "circle-help",
  Pages: "layout-dashboard",
  Personal: "bookmark",
  Views: "layout-dashboard",
  Objects: "boxes",
  Roles: "users",
  Jobs: "briefcase",
  Candidates: "user",
  Recent: "clock",
};

// The dock searches "the page you're on" first (docs/personal/SCOPED-SEARCH-PRD.md):
// map the current path to a search scope. `key` is sent to /api/search (which runs
// that scope's own leg first); `label` is the group header + the scope chip; `href`
// is the surface the "Search all of <label>" row drives; `icon` marks the chip +
// dropdown. Extend as sections gain a scope leg — Personal is the first.
interface DockScope {
  key: string;
  label: string;
  href: string;
  icon: string;
  /**
   * Demote-don't-delete: hidden scopes stay registered so scopeForPath/search
   * legs still resolve, but they are not listed in the dock switcher and do not
   * auto-scope the page.
   */
  hidden?: boolean;
}

// Every indexed scope the dock can search (each has its own leg in
// `/api/search` + a `?q=` destination). The Slack-style scope switcher offers
// the visible ones; adding one is a single entry here + a `scopeHits` branch
// server-side. Hidden entries stay so old deep links do not break search legs.
const SCOPES: readonly DockScope[] = [
  { key: "personal", label: "Personal", href: "/personal", icon: "bookmark", hidden: true },
  // The Customers mode overview + its data-cuts (List/Board/Forecast) all live at
  // `/customers` and show the account portfolio — searching here searches the
  // account base first. `href` drives the "Search all of Customers" row → the
  // filtered overview (`/customers?q=`). M5 mothball: hidden from the default
  // switcher; still resolvable when already on the page.
  { key: "customers", label: "Customers", href: "/customers", icon: "building-2", hidden: true },
  { key: "database", label: "Tables", href: "/database", icon: "database" },
  { key: "database-views", label: "Views", href: "/database/views", icon: "layout-dashboard" },
  { key: "database-objects", label: "Objects", href: "/database/objects", icon: "boxes" },
  { key: "projects", label: "Projects", href: "/projects", icon: "folder" },
  // Hiring is a section (mounted per-project at `/projects/<slug>/hiring/*`), so the
  // "Search all of Jobs/Candidates" row routes through the modes deep-link, which
  // round-trips to the same view at the workspace tier from any project slug.
  { key: "hiring-jobs", label: "Jobs", href: "/operations?area=hiring&sub=jobs", icon: "briefcase", hidden: true },
  { key: "hiring-candidates", label: "Candidates", href: "/operations?area=hiring&sub=candidates", icon: "user", hidden: true },
  // CRM leads live in the section-hosted sales view (`/projects/<slug>/sales/leads`);
  // the "Search all of Leads" row routes through the modes deep-link, which
  // round-trips to the same view at the workspace tier from any project slug.
  { key: "crm-leads", label: "Leads", href: "/customers?area=sales&sub=leads", icon: "user", hidden: true },
  // Finance is a query-routed mode domain (`/operations?area=finance`), so its
  // scope matches on the area param, not a path prefix (see scopeForPath).
  { key: "finance", label: "Finance", href: "/operations?area=finance", icon: "dollar-sign", hidden: true },
  // Rooms (Work ▸ Chat) — channel titles + message bodies. Path form `/work/chat`
  // and legacy `?area=chat` both resolve (see scopeForPath).
  { key: "rooms", label: "Chat", href: "/work/chat", icon: "messages-square", hidden: true },
  // The Help center is search-first: on `/help` the dock searches the article
  // corpus; the "Search all of Help" row drives `/help?q=` (the filtered list).
  { key: "help", label: "Help", href: "/help", icon: "circle-help" },
];

function visibleScopes(): readonly DockScope[] {
  return SCOPES.filter((s) => !s.hidden);
}

// The active scope keys on the pathname, and — for query-routed mode domains
// (Finance, the sales CRM) — on the mode's `area`/`sub` params too, since those
// views share a single `/operations` or `/customers` path.
function scopeForPath(
  pathname: string | null,
  area?: string | null,
  sub?: string | null,
): DockScope | null {
  if (!pathname) return null;
  if (pathname === "/personal" || pathname.startsWith("/personal/")) {
    return SCOPES.find((s) => s.key === "personal") ?? null;
  }
  // The database inventory (the tables index + the schema map). The more specific
  // `/database/views` + `/database/objects` surfaces have their own scopes below.
  if (pathname === "/database" || pathname === "/database/schema") {
    return SCOPES.find((s) => s.key === "database") ?? null;
  }
  // The Help center landing — search the article corpus first.
  if (pathname === "/help") {
    return SCOPES.find((s) => s.key === "help") ?? null;
  }
  if (pathname === "/database/views" || pathname.startsWith("/database/views/")) {
    return SCOPES.find((s) => s.key === "database-views") ?? null;
  }
  if (pathname === "/database/objects" || pathname.startsWith("/database/objects/")) {
    return SCOPES.find((s) => s.key === "database-objects") ?? null;
  }
  // The projects management board (its subpaths — map, a project — have their
  // own scopes/none, so match the board root exactly).
  if (pathname === "/projects") {
    return SCOPES.find((s) => s.key === "projects") ?? null;
  }
  // Hiring section views are mounted per-project (`/projects/<slug>/hiring/jobs`,
  // `…/hiring/candidates`) — match the section-deep path, not a fixed prefix.
  if (/^\/projects\/[^/]+\/hiring\/jobs(\/|$)/.test(pathname)) {
    return SCOPES.find((s) => s.key === "hiring-jobs") ?? null;
  }
  if (/^\/projects\/[^/]+\/hiring\/candidates(\/|$)/.test(pathname)) {
    return SCOPES.find((s) => s.key === "hiring-candidates") ?? null;
  }
  // CRM leads: the section-hosted sales Leads view (per-project deep path) OR the
  // workspace-tier query route (`/customers?area=sales&sub=leads`).
  if (
    /^\/projects\/[^/]+\/sales\/leads(\/|$)/.test(pathname) ||
    (pathname === "/customers" && area === "sales" && sub === "leads")
  ) {
    return SCOPES.find((s) => s.key === "crm-leads") ?? null;
  }
  // Finance is a query-routed mode domain — the ledger lives at
  // `/operations?area=finance`, not a dedicated path segment.
  if (pathname === "/operations" && area === "finance") {
    return SCOPES.find((s) => s.key === "finance") ?? null;
  }
  // Rooms (Work ▸ Chat): path form `/work/chat` (label-slug) OR the legacy
  // query route `/work?area=chat` (and `/work` with area=chat after path flip).
  if (
    pathname === "/work/chat" ||
    pathname.startsWith("/work/chat/") ||
    (pathname === "/work" && area === "chat")
  ) {
    return SCOPES.find((s) => s.key === "rooms") ?? null;
  }
  // The Customers mode overview + its data-cuts (List/Board/Forecast/Properties)
  // all share the bare `/customers` path (the cut is a `?view=`) and show the
  // account portfolio. The sales-leads clause above already claimed the
  // `?area=sales&sub=leads` sub-domain, so a bare `/customers` scopes to the
  // account base (searching accounts / customers first).
  if (pathname === "/customers") {
    return SCOPES.find((s) => s.key === "customers") ?? null;
  }
  return null;
}

function scopeForKey(key: string | null | undefined): DockScope | null {
  return key ? SCOPES.find((s) => s.key === key) ?? null : null;
}

const STRONG_ASK = ["explain", "write", "draft", "summarize", "summarise", "compose", "plan"];
const SOFT_ASK = ["what", "why", "how", "who", "when", "should", "can", "could", "is", "are", "do", "does"];

function detectRoute(q: string): Route {
  const t = q.trim().toLowerCase();
  if (!t) return "search";
  if (t.endsWith("?")) return "chat";
  const words = t.split(/\s+/);
  const first = words[0] ?? "";
  if (STRONG_ASK.includes(first)) return "chat";
  if (SOFT_ASK.includes(first) && words.length >= 3) return "chat";
  return "search";
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

// ─── recents (localStorage) ──────────────────────────────────────────────────

const RECENT_KEY = "dock:recent";

function readRecents(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, 5) : [];
  } catch {
    return [];
  }
}
function pushRecent(q: string) {
  if (typeof window === "undefined" || !q.trim()) return;
  try {
    const next = [q.trim(), ...readRecents().filter((r) => r !== q.trim())].slice(0, 5);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

// ─── flat-row model (uniform keyboard nav across commands/hits/ask/recents) ───

interface FlatRow {
  key: string;
  section: string;
  icon: string;
  title: string;
  subtitle?: string;
  shortcut?: string;
  accent?: boolean;
  activate: () => void;
}

// ─── blur halo ───────────────────────────────────────────────────────────────
// A progressive (variable) blur behind the pill: several stacked backdrop-blur
// layers, each with a heavier radius but a tighter, feathered radial mask. Where
// they overlap, the blur ramps smoothly from none at the rim to heavy against the
// pill — and because every layer's mask fades to transparent *inside* its own box,
// the halo is a soft rounded glow with no hard line of blur.
const HALO_LAYERS = [
  { blur: 3, solid: 55, fade: 92 },
  { blur: 7, solid: 40, fade: 78 },
  { blur: 13, solid: 24, fade: 62 },
  { blur: 22, solid: 0, fade: 44 },
] as const;

function DockHalo() {
  return (
    <div aria-hidden className="pointer-events-none absolute -inset-x-32 -inset-y-12 -z-10">
      {HALO_LAYERS.map((l, i) => {
        // ellipse radius = half the box, so the feather always completes inside the box
        // (no clipped edge → no hard line) and the falloff reads as a soft rounded glow
        const mask = `radial-gradient(50% 50% at 50% 50%, black ${l.solid}%, transparent ${l.fade}%)`;
        return (
          <div
            key={i}
            className="absolute inset-0"
            style={{
              backdropFilter: `blur(${l.blur}px)`,
              WebkitBackdropFilter: `blur(${l.blur}px)`,
              maskImage: mask,
              WebkitMaskImage: mask,
            }}
          />
        );
      })}
    </div>
  );
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

// ─── component ─────────────────────────────────────────────────────────────

export function DockBar({
  copilotEnabled,
  copilotDevelop,
  pages = [],
  composer = "dock",
}: {
  copilotEnabled: boolean;
  copilotDevelop: boolean;
  pages?: DockPageSpec[];
  /** `hidden` keeps chat listeners mounted but does not paint the search pill. */
  composer?: "dock" | "hidden";
}) {
  const router = useNavigate();
  const pathname = usePathname();
  const searchParams = useSearch();
  const { mutate } = useSWRConfig();
  const sessionAccount = useApi(auth.me, {});
  // Bind live intake to the authenticated server identity, never chrome labels.
  // Retire the prior subscription while authentication is being revalidated.
  const authorizationScope = !sessionAccount.isLoading && !sessionAccount.isValidating &&
    !sessionAccount.error && sessionAccount.data?.authenticated === true &&
    sessionAccount.data.userId != null && sessionAccount.data.workspaceId != null
      ? `user:${sessionAccount.data.userId}/workspace:${sessionAccount.data.workspaceId}`
      : undefined;

  // Which surface the dock is searching. `pageScope` is the page you're on (the
  // default). Slack-style, the dock prepends that as a tag and lets you *change*
  // it: `scopeChoice` is the user's override — `undefined` follows the page,
  // `null` means "Everything" (unscoped), a key means that scope. `activeScope`
  // resolves the two and drives the leg, the group ordering, the chip + the
  // "Search all of …" row (SCOPED-SEARCH-PRD §3.3).
  // The mode `area`/`sub` params disambiguate query-routed domains (Finance, the
  // sales CRM) that share one path.
  const modeArea = searchParams.get("area");
  const modeSub = searchParams.get("sub");
  const pageScope = React.useMemo(() => {
    const found = scopeForPath(pathname, modeArea, modeSub);
    return found && !found.hidden ? found : null;
  }, [pathname, modeArea, modeSub]);
  const [scopeChoice, setScopeChoice] = React.useState<string | null | undefined>(undefined);
  const [scopeMenuOpen, setScopeMenuOpen] = React.useState(false);
  const activeScope =
    scopeChoice === undefined ? pageScope : scopeForKey(scopeChoice);
  // A new page resets the override so search re-scopes to where you now are.
  React.useEffect(() => setScopeChoice(undefined), [pageScope?.key]);

  const [query, setQuery] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  const [sel, setSel] = React.useState(0);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [recents, setRecents] = React.useState<string[]>([]);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const blurTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedQuery = useDebounced(query, 150);
  const typing = query.trim().length > 0;
  const route: Route = detectRoute(query);

  React.useEffect(() => setRecents(readRecents()), [focused]);

  // ── chat state (SI-2-02: session client, not chatStream transport) ─────────
  /** Live session URN (`live_session:<id>`). Null = no open chat session. */
  const [activeSessionUrn, setActiveSessionUrn] = React.useState<string | null>(null);
  /** Projection conversation uid (excellent_chat_conversations) — for history list. */
  const [activeUid, setActiveUid] = React.useState<string | null>(null);
  const [optimistic, setOptimistic] = React.useState<string | null>(null);
  const [resolvedPerms, setResolvedPerms] = React.useState<Set<string>>(new Set());
  const alwaysAllow = React.useRef<Set<string>>(new Set());
  const creatingRef = React.useRef(false);

  // Dismiss + background-status. The dock is the app's only chat surface, so a
  // streaming turn must be put-away-able WITHOUT aborting it: `dismissed`
  // overrides the busy-force-open of the panel (below); `unread` flags a reply
  // that landed while the panel was away. Re-opening (focusing the bar) clears
  // both. `dismissedRef` lets the async send-completion read the latest flag.
  const [dismissed, setDismissed] = React.useState(false);
  const [unread, setUnread] = React.useState(false);
  const dismissedRef = React.useRef(dismissed);
  dismissedRef.current = dismissed;

  // The chat-header model picker's menu is portaled (its open menu blurs the
  // root), so its open state must hold the panel open like `historyOpen` does.
  const [modelMenuOpen, setModelMenuOpen] = React.useState(false);

  // Ambient capture (Universal Intake) — the run/offer state, paste + capture
  // handlers, and global-drop effect live in one unit (INTAKE-PRD §2.8.3); the
  // dock reads `intake.active` for panel gating and wires `intake.onPaste` on
  // the input. `focusInput` is hoisted below (a function declaration).
  const intake = useDockIntake({ focusInput, setQuery });

  // Session client — authoritative reduced chat state (S-D4 server-wins).
  const session = useSession<ChatState>(activeSessionUrn, {
    mode: "actor",
    authorizationScope,
    enabled: Boolean(activeSessionUrn) && copilotEnabled && authorizationScope !== undefined,
    reduce: reduceChatClient,
    initialState: initialChatState,
  });

  // Keep projection conversation uid in sync with session state (for history).
  React.useEffect(() => {
    const uid = session.state?.conversationUid;
    if (uid && uid !== activeUid) setActiveUid(uid);
  }, [session.state?.conversationUid, activeUid]);

  // Map reduced session messages → ChatMessage shape for ChatTranscript.
  const messages: ChatMessage[] = React.useMemo(() => {
    const rows = session.state?.messages ?? [];
    const tools = session.state?.tools ?? [];
    return rows.map((m) => ({
      uid: m.id,
      role: m.role,
      content: m.content,
      worker_run_id: null,
      created_at: "",
      tool_calls: tools
        .filter((t) => t.messageId === m.id || (!t.messageId && m.role === "assistant"))
        .map((t) => ({
          uid: t.id,
          tool_name: t.name,
          input_json: t.inputJson ?? null,
          status: t.status,
          verdict_reason: t.reason ?? null,
          result_json: t.summary ?? null,
          created_at: "",
        })),
      input_tokens: null,
      output_tokens: null,
      cost_usd: null,
    })) as ChatMessage[];
  }, [session.state?.messages, session.state?.tools]);

  const convList = useApi(chat.list, {});
  const conversations = convList.data?.conversations ?? [];

  // Live tools/permissions from reduced session state (no stream frames).
  const live = React.useMemo(() => {
    const tools = (session.state?.tools ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      summary: t.summary,
      status: t.status,
      reason: t.reason ?? undefined,
    }));
    const permissions = (session.state?.permissions ?? [])
      .filter((p) => p.status === "pending" && !resolvedPerms.has(p.id))
      .map((p) => ({
        id: p.id,
        tool: p.tool,
        input: p.inputJson ? safeJsonParse(p.inputJson) : undefined,
        risk: (p.risk as "read" | "mutate" | "forbidden") ?? "mutate",
        reason: p.reason ?? undefined,
      }));
    return {
      text: "",
      thinking: "",
      todos: null as Array<{ content: string; status: string }> | null,
      tools,
      permissions,
      usage: null,
      agentSwitch: null,
    };
  }, [session.state?.tools, session.state?.permissions, resolvedPerms]);

  // Busy while connecting, or last reduced message is still a user turn
  // (host-side agent turn in flight), or local optimistic pending.
  const lastReduced = session.state?.messages[session.state.messages.length - 1];
  const busy =
    session.connectionState === "connecting" ||
    session.connectionState === "reconnecting" ||
    optimistic != null ||
    (session.connectionState === "connected" && lastReduced?.role === "user");

  const hasLiveWork =
    live.text.length > 0 ||
    live.tools.length > 0 ||
    live.thinking.length > 0 ||
    live.todos !== null ||
    live.permissions.length > 0;
  const lastMsg = messages[messages.length - 1];
  const persistedHasAnswer =
    lastMsg?.role === "assistant" && (lastMsg.content?.trim().length ?? 0) > 0;
  // Show the live turn whenever a turn is in flight (not only once the first
  // frame lands) so the "Thinking…" working line appears the instant you send.
  const showLive = !persistedHasAnswer && (busy || hasLiveWork);
  const showOptimistic =
    optimistic !== null &&
    !messages.some((m) => m.role === "user" && m.content === optimistic);
  const hasThread = messages.length > 0 || showOptimistic || showLive || busy;

  // ── search ──────────────────────────────────────────────────────────────────
  const { data: searchData } = useApi(
    search.shellGet,
    focused && typing && route === "search"
      ? { query: { q: debouncedQuery, scope: activeScope?.key } }
      : null,
    { keepPreviousData: true },
  );
  const hits = searchData?.hits ?? [];

  // ── commands (palette parity) ────────────────────────────────────────────────
  const navigate = React.useCallback(
    (href: string) => {
      pushRecent(query);
      router.push(href);
      blurInput();
    },
    [query, router],
  );

  const commands = React.useMemo<FlatRow[]>(
    () => [
      {
        key: "cmd:new-task",
        section: "Commands",
        icon: "plus",
        title: "Create new issue",
        shortcut: "C",
        activate: () => {
          window.dispatchEvent(new CustomEvent("excellent:create-task"));
          blurInput();
        },
      },
      {
        key: "cmd:new-goal",
        section: "Commands",
        icon: "target",
        title: "Create new goal",
        activate: () => {
          window.dispatchEvent(new CustomEvent("excellent:create-goal"));
          blurInput();
        },
      },
      {
        key: "cmd:new-project",
        section: "Commands",
        icon: "folder-plus",
        title: "Create new project",
        shortcut: "N P",
        activate: () => {
          window.dispatchEvent(new CustomEvent("excellent:create-project"));
          blurInput();
        },
      },
      {
        key: "cmd:settings",
        section: "Commands",
        icon: "settings",
        title: "Open settings",
        shortcut: ",",
        activate: () => navigate("/settings"),
      },
    ],
    [navigate],
  );

  // ── chat actions (mirror CopilotPanel) ────────────────────────────────────────
  const resolvePermission = React.useCallback(
    async (id: string, decision: "allow" | "deny", always: boolean) => {
      if (always) {
        const tool = live.permissions.find((p) => p.id === id)?.tool;
        if (tool) alwaysAllow.current.add(tool);
      }
      setResolvedPerms((prev) => new Set(prev).add(id));
      try {
        if (activeSessionUrn) {
          await session.act(
            { type: "chat.permission.resolve", id, decision },
            `chat.permission.resolve:${id}:${decision}`,
          );
        } else if (activeUid) {
          await apiCall(chat.resolvePermission, {
            params: { id: activeUid },
            body: { permission: id, decision },
          });
        }
      } catch {
        /* MCP-side timeout (deny) is the backstop */
      }
    },
    [activeSessionUrn, activeUid, live.permissions, session],
  );

  React.useEffect(() => {
    for (const p of live.permissions) {
      if (alwaysAllow.current.has(p.tool) && !resolvedPerms.has(p.id)) {
        void resolvePermission(p.id, "allow", false);
      }
    }
  }, [live.permissions, resolvedPerms, resolvePermission]);

  /** Ensure a live chat session exists; returns session urn. */
  async function ensureSession(
    opts: { conversationUid?: string } = {},
  ): Promise<string | null> {
    if (activeSessionUrn && !opts.conversationUid) return activeSessionUrn;
    if (creatingRef.current) return null;
    creatingRef.current = true;
    try {
      const res = await apiCall(sessions.create, {
        body: {
          kind: "chat",
          mode: copilotDevelop ? "develop" : "operate",
          ...(opts.conversationUid
            ? { conversationUid: opts.conversationUid }
            : {}),
        },
      });
      setActiveSessionUrn(res.urn);
      return res.urn;
    } finally {
      creatingRef.current = false;
    }
  }

  async function sendChat(text: string) {
    const t = text.trim();
    if (!t || !copilotEnabled) return;
    setQuery("");
    setHistoryOpen(false);
    // Sending re-engages the panel — clear any prior dismiss/unread state.
    setDismissed(false);
    setUnread(false);
    const wasNew = !activeSessionUrn;
    const urn = await ensureSession();
    if (!urn) return;
    setOptimistic(t);
    setResolvedPerms(new Set());
    const msgId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `msg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try {
      await session.act(
        { type: "chat.user", id: msgId, content: t },
        `chat.user:${msgId}`,
      );
      if (wasNew) mutate(apiKey(chat.list, {}));
    } catch {
      /* session will surface error via connectionState */
    }
    setOptimistic(null);
    // Turn finished while the panel was put away → flag the reply unread so the
    // collapsed bar shows "Reply ready" until the user clicks back in.
    // Host-side agent turn is async; mark unread if still dismissed after act.
    if (dismissedRef.current) setUnread(true);
  }

  function newConversation() {
    setOptimistic(null);
    setResolvedPerms(new Set());
    setActiveSessionUrn(null);
    setActiveUid(null);
    setHistoryOpen(false);
    setQuery("");
    focusInput();
  }

  async function selectConversation(uid: string) {
    setOptimistic(null);
    setResolvedPerms(new Set());
    setActiveUid(uid);
    setHistoryOpen(false);
    setQuery("");
    // Resume: open a new live session rebound to this conversation projection.
    setActiveSessionUrn(null);
    await ensureSession({ conversationUid: uid });
    focusInput();
  }

  async function handleArchive(uid: string) {
    // Prefer session act when this conversation is the active session.
    if (uid === activeUid && activeSessionUrn) {
      try {
        await session.act({ type: "chat.archive" }, `chat.archive:${uid}`);
      } catch {
        await apiCall(chat.archive, { params: { id: uid } });
      }
    } else {
      await apiCall(chat.archive, { params: { id: uid } });
    }
    if (uid === activeUid) {
      setOptimistic(null);
      setActiveSessionUrn(null);
      setActiveUid(null);
    }
    mutate(apiKey(chat.list, {}));
  }

  async function handleRename(uid: string, title: string) {
    await apiCall(chat.rename, { params: { id: uid }, body: { title } });
    mutate(apiKey(chat.list, {}));
  }

  // Change the search scope from the token's ranked dropdown. `null` = "Everything"
  // (unscoped); a key = that scope. Picking the page's own scope returns to
  // "follow the page" (undefined) — same result, cleaner intent. Refocus so the
  // (already-typed) query re-runs against the new scope without a second click.
  function pickScope(key: string | null) {
    setScopeChoice(pageScope && key === pageScope.key ? undefined : key);
    focusInput();
  }

  // ── focus plumbing ────────────────────────────────────────────────────────────
  function focusInput() {
    setTimeout(() => inputRef.current?.focus(), 0);
  }
  function blurInput() {
    inputRef.current?.blur();
    setFocused(false);
  }
  function handleRootFocus() {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setFocused(true);
  }
  function handleRootBlur(e: React.FocusEvent<HTMLDivElement>) {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    blurTimer.current = setTimeout(() => setFocused(false), 120);
  }

  // Close the chat panel WITHOUT aborting an in-flight turn (× ≠ Stop). The turn
  // keeps streaming in the background; the collapsed bar carries `bgStatus` until
  // you click back in. Stop (the header button / Esc while busy) is the abort.
  function closeChat() {
    setDismissed(true);
    blurInput();
  }
  // Re-open from the collapsed bar: drop back into the live thread, clear status.
  function reopenChat() {
    setDismissed(false);
    setUnread(false);
    focusInput();
  }

  // ── external triggers: open-search event, ⌘K, "/" ────────────────────────────
  React.useEffect(() => {
    function open() {
      focusInput();
    }
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (document.activeElement === inputRef.current) inputRef.current?.blur();
        else focusInput();
      }
    }
    window.addEventListener("open-search", open);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("open-search", open);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  // ── chat entry points: this bottom dock is the app's ONE chat surface ─────────
  // Every "chat about this" affordance in the app routes here — the side rail is
  // retired (no more chat opening to the right). Two signals:
  //   · `excellent:ask-agent {query}` — a seeded ask (Pulse synthesis phrase, a
  //     Play card). Prefill the bar + focus so the user reviews and sends; the
  //     seeds are questions, so detectRoute lands on the "Ask" affordance (Enter
  //     sends). Mirrors the old rail composer's prefill-not-autosend handoff.
  //   · `excellent:open-chat` — the user-menu "Chat" entry. Open the chat-history
  //     panel (New chat + recent chats) and focus, a clear chat home.
  React.useEffect(() => {
    function onAsk(e: Event) {
      const q = (e as CustomEvent<{ query?: string }>).detail?.query?.trim();
      setHistoryOpen(false);
      if (q) setQuery(q);
      focusInput();
    }
    function onOpenChat() {
      if (copilotEnabled) setHistoryOpen(true);
      focusInput();
    }
    window.addEventListener("excellent:ask-agent", onAsk);
    window.addEventListener("excellent:open-chat", onOpenChat);
    return () => {
      window.removeEventListener("excellent:ask-agent", onAsk);
      window.removeEventListener("excellent:open-chat", onOpenChat);
    };
  }, [copilotEnabled]);

  // ── flat rows for the results / history panels ────────────────────────────────
  const filteredConvos = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => (c.title ?? "").toLowerCase().includes(q));
  }, [conversations, query]);

  const resultRows = React.useMemo<FlatRow[]>(() => {
    if (!typing) {
      const cmds = commands;
      const rec: FlatRow[] = recents.map((r) => ({
        key: `recent:${r}`,
        section: "Recent",
        icon: "clock",
        title: r,
        activate: () => setQuery(r),
      }));
      return [...cmds, ...rec];
    }
    const q = query.trim().toLowerCase();
    const cmdMatches = commands.filter((c) => c.title.toLowerCase().includes(q));
    const pageRows: FlatRow[] = pages
      .filter((p) => p.label.toLowerCase().includes(q))
      .map((p) => ({
        key: `page:${p.href}`,
        section: "Pages",
        icon: "layout-dashboard",
        title: p.label,
        subtitle: p.hint,
        activate: () => navigate(p.href),
      }));
    const toHitRow = (h: SearchHit): FlatRow => ({
      key: `hit:${h.id}`,
      section: h.group,
      icon: GROUP_ICON[h.group] ?? "file-text",
      title: h.label,
      subtitle: h.hint,
      activate: () => navigate(h.href),
    });
    // Scope group (SCOPED-SEARCH-PRD): a "search all of <scope>" row that drives the
    // scope's own surface (Personal → the board, filtered), then the scope's live
    // hits — placed FIRST so the page you're on leads the results. Suppressed when
    // the user switches the scope to "Everything" (activeScope === null).
    const scopeHits = activeScope ? hits.filter((h) => h.group === activeScope.label) : [];
    const otherHits = activeScope ? hits.filter((h) => h.group !== activeScope.label) : hits;
    const scopeGroup: FlatRow[] = activeScope
      ? [
          {
            key: "scope:all",
            section: activeScope.label,
            icon: "search",
            title: `Search all of ${activeScope.label} for “${query.trim()}”`,
            subtitle: "Open the filtered board",
            accent: true,
            activate: () => navigate(`${activeScope.href}?q=${encodeURIComponent(query.trim())}`),
          },
          ...scopeHits.map(toHitRow),
        ]
      : [];
    // Everything else, grouped in canonical order.
    const order = [...HIT_GROUP_ORDER] as string[];
    const otherHitRows: FlatRow[] = [...otherHits]
      .sort((a, b) => {
        const ai = order.indexOf(a.group);
        const bi = order.indexOf(b.group);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      })
      .map(toHitRow);
    const askRow: FlatRow = {
      key: "ask",
      section: "Assistant",
      icon: "sparkles",
      title: copilotEnabled ? `Ask “${query.trim()}”` : "Enable chat to ask",
      subtitle: copilotEnabled ? "Answer from your workspace" : "Settings → Agents → Chat",
      accent: true,
      activate: () => (copilotEnabled ? sendChat(query) : navigate("/settings/agents/copilot")),
    };
    // Bare-URL branch (§2.8.3): offer to summarize the page into the workspace via
    // intake (mode:'url'). Honest copy — until core URL fetch lands (slice 6) it
    // holds as raw/awaiting; the Play card + ledger carry the follow-up.
    const urlRow: FlatRow | null = looksLikeUrl(query)
      ? {
          key: "intake:url",
          section: "Import",
          icon: "globe",
          title: "Summarize & file this page",
          subtitle: "Land the page into your workspace — a summary note",
          accent: true,
          activate: () => void intake.runIntake({ name: query.trim(), text: query.trim(), mode: "url" }),
        }
      : null;
    const head = urlRow ? [urlRow] : [];
    return route === "chat"
      ? [...head, askRow, ...scopeGroup, ...cmdMatches, ...pageRows, ...otherHitRows]
      : [...head, ...scopeGroup, ...cmdMatches, ...pageRows, ...otherHitRows, askRow];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typing, query, route, hits, commands, pages, recents, copilotEnabled, activeScope]);

  // ── what the panel shows ──────────────────────────────────────────────────────
  // `dismissed` is the override the rail never had: it suppresses the auto-open
  // (including the busy-force-open) so a streaming turn can be put away. Focusing
  // the bar clears it (reopenChat / the input's onFocus below).
  // An active intake offer/run/outcome takes the panel over its own inline surface
  // (§2.8.3 — the dock hosts the ambient-capture outcome, no toast).
  let panel: "results" | "thread" | "history" | "intake" | null = null;
  if (intake.active) panel = "intake";
  else if (!dismissed && (focused || historyOpen || busy || modelMenuOpen || scopeMenuOpen)) {
    if (historyOpen) panel = "history";
    else if (typing) panel = "results";
    else if (hasThread) panel = "thread";
    else panel = "results";
  }
  const panelOpen = panel !== null;

  // Background status for the collapsed bar: a turn put away with × is either
  // still working, or has a reply waiting (unread) until you click back in.
  const bgStatus: "working" | "reply" | null =
    dismissed && busy ? "working" : dismissed && unread ? "reply" : null;

  const historyRowCount = filteredConvos.length + 1; // + "New chat"
  const navRowCount = panel === "history" ? historyRowCount : resultRows.length;

  React.useEffect(() => setSel(0), [query, historyOpen, panel]);

  function activateSel() {
    if (panel === "history") {
      if (sel === 0) newConversation();
      else {
        const c = filteredConvos[sel - 1];
        if (c) selectConversation(c.uid);
      }
      return;
    }
    const r = resultRows[sel];
    if (r) r.activate();
    else if (typing && route === "chat" && copilotEnabled) sendChat(query);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (panel === "thread" && route === "chat" && copilotEnabled && typing) sendChat(query);
      else activateSel();
    } else if (e.key === "Escape") {
      if (busy) {
        // Session turns are host-owned; Esc dismisses the panel (does not abort).
        setDismissed(true);
      } else if (historyOpen) {
        setHistoryOpen(false);
      } else if (query) {
        setQuery("");
      } else {
        blurInput();
      }
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (panel !== "results" && panel !== "history") return;
      e.preventDefault();
      setSel((s) => {
        const n = navRowCount;
        if (n === 0) return 0;
        return e.key === "ArrowDown" ? (s + 1) % n : (s - 1 + n) % n;
      });
    }
    // Single-key command shortcuts when empty (palette parity): "C" / ","
    else if (!typing && !historyOpen && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const k = e.key.toLowerCase();
      const cmd = commands.find((c) => typeof c.shortcut === "string" && c.shortcut.toLowerCase() === k);
      if (cmd) {
        e.preventDefault();
        cmd.activate();
      }
    }
  }

  const leadingIcon = historyOpen ? "history" : route === "chat" ? "sparkles" : "search";
  const placeholder = historyOpen
    ? "Search your chats…"
    : bgStatus === "working"
      ? "Working… click to watch"
      : bgStatus === "reply"
        ? "Reply ready — click to read"
        : activeScope
          ? `Search ${activeScope.label} or ask anything…`
          : "Search or ask anything…";

  const showComposer = composer !== "hidden";
  const showChat = panel === "thread" || panel === "intake";
  if (!showComposer && !showChat) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-24 z-40 flex justify-center px-4 md:bottom-7"
      onFocus={handleRootFocus}
      onBlur={handleRootBlur}
    >
      <div className="pointer-events-auto flex w-[min(34rem,100%)] flex-col items-stretch gap-2">
        {/* floating panel — grows upward */}
        {panelOpen && (
          <div className="animate-in fade-in-0 slide-in-from-bottom-2 overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-surface-5 backdrop-blur-xl duration-150">
            {/* Chat header — the × closes the panel but KEEPS the turn running
                (Close ≠ Stop). Stop aborts. Only on the thread panel; results /
                history close on blur. */}
            {panel === "thread" && (
              <div className="flex min-h-9 shrink-0 items-center justify-between border-b border-border/40 px-3">
                <span className="flex items-center gap-1.5 type-subtext text-muted-foreground">
                  <ChatMark size="sm" /> Chat
                  {busy && <span className="text-muted-foreground/60">· working…</span>}
                </span>
                <div className="flex items-center gap-0.5">
                  {/* §14 M4 — provider+model toggle; renders nothing until Settings
                      saves a selection or the branch switch is on. */}
                  <DockModelPicker onOpenChange={setModelMenuOpen} />
                  {busy && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label="Stop"
                      title="Close panel — host continues the turn"
                      onClick={() => setDismissed(true)}
                    >
                      <Icon name="square" className="size-3" /> Stop
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="icon-round-sm"
                    variant="ghost"
                    aria-label="Close chat"
                    title="Close — keeps running"
                    onClick={closeChat}
                  >
                    <Icon name="x" />
                  </Button>
                </div>
              </div>
            )}
            {panel === "intake" ? (
              <IntakePanel
                offer={intake.pasteOffer}
                run={intake.run}
                onImport={() =>
                  intake.pasteOffer &&
                  intake.runIntake({ name: "Pasted table", text: intake.pasteOffer.text, mode: "text" })
                }
                onPasteAsText={intake.pasteAsText}
                onDismiss={intake.reset}
              />
            ) : panel === "thread" ? (
              <ChatTranscript
                messages={messages}
                optimistic={showOptimistic ? optimistic : null}
                live={live}
                frames={[]}
                showLive={showLive}
                busy={busy}
                error={session.error}
                onResolvePermission={resolvePermission}
              />
            ) : panel === "history" ? (
              <HistoryPanel
                query={query}
                conversations={filteredConvos}
                activeUid={activeUid}
                sel={sel}
                onHover={setSel}
                onNew={newConversation}
                onSelect={selectConversation}
                onRename={handleRename}
                onArchive={handleArchive}
              />
            ) : (
              <ResultsPanel rows={resultRows} sel={sel} onHover={setSel} typing={typing} />
            )}
            {(panel === "results" || panel === "history") && <PanelFooterHint />}
          </div>
        )}

        {/* the low-profile bar — a progressive blur halo defines the pill against page content */}
        {showComposer ? (
        <div className="relative">
          <DockHalo />
          {/* progress hairline — a background turn is still streaming while put away */}
          {bgStatus === "working" && (
            <div className="pointer-events-none absolute -top-px left-5 right-5 z-10 h-0.5 overflow-hidden rounded-full">
              <div className="dock-progress-bar h-full w-1/3 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent" />
            </div>
          )}
          <div
            className={cn(
              "flex h-10 items-center gap-1.5 rounded-full border bg-background/80 px-2 backdrop-blur-xl transition-all duration-150",
              // Focus is the load-bearing state: a scoped-search button focuses the
              // dock, so the bar must visibly "catch" — a primary ring + tinted
              // border + lifted shadow make it unmistakably the active target.
              focused
                ? "border-primary/70 shadow-surface-5 ring-2 ring-primary/40"
                : "border-border/60 shadow-surface-3",
              panelOpen && !focused && "shadow-surface-4",
              bgStatus === "reply" && !focused && "ring-1 ring-primary/40",
            )}
          >
          <span className="grid size-7 shrink-0 place-items-center rounded-full text-muted-foreground">
            <Icon name={leadingIcon} className={cn(route === "chat" && !historyOpen && "text-primary")} />
          </span>

          {/* scope token (Slack-style) — the dock prepends the page you're on as a
              tag and lets you *change* it via a ranked dropdown (SCOPED-SEARCH-PRD
              §3.3); hidden once you shift into chat/history. */}
          {pageScope && !historyOpen && route !== "chat" && !bgStatus ? (
            <ScopeChip
              pageScope={pageScope}
              activeScope={activeScope}
              onPick={pickScope}
              onOpenChange={setScopeMenuOpen}
            />
          ) : null}

          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={intake.onPaste}
            // Clicking back into the bar re-opens a put-away chat (the door back
            // in) and clears its status. Done on the input (not the root) so the
            // panel's own × / Stop buttons don't re-open it as they take focus.
            onFocus={() => {
              if (dismissed) {
                setDismissed(false);
                setUnread(false);
              }
            }}
            placeholder={placeholder}
            aria-label={historyOpen ? "Search chats" : "Search or ask"}
            className="min-w-0 flex-1 bg-transparent type-body text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
          />

          {bgStatus ? (
            /* button-ignore: the collapsed-bar background-status chip (spinner/dot
               + label, click to re-open the put-away chat), not a Button-atom action. */
            <button
              type="button"
              onClick={reopenChat}
              aria-label={
                bgStatus === "working"
                  ? "Chat is working — click to watch"
                  : "Reply ready — click to read"
              }
              className={cn(
                "flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 type-subtext transition-colors",
                bgStatus === "working"
                  ? "bg-muted text-muted-foreground hover:text-foreground"
                  : "bg-primary/10 text-primary hover:bg-primary/15",
              )}
            >
              {bgStatus === "working" ? (
                <>
                  <Icon name="loader" className="size-3 animate-spin" /> Working…
                </>
              ) : (
                <>
                  <span className="size-1.5 rounded-full bg-primary" /> Reply ready
                </>
              )}
            </button>
          ) : busy ? (
            <span className="hidden shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-1 type-subtext text-muted-foreground sm:flex">
              <Icon name="loader" className="size-3 animate-spin" /> Esc to stop
            </span>
          ) : typing ? (
            <span className="hidden shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-1 type-subtext text-muted-foreground sm:flex">
              <Icon name="corner-down-left" className="size-3" />
              {route === "chat" ? "Ask" : "Search"}
            </span>
          ) : !historyOpen ? (
            <kbd className="mr-0.5 hidden shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 type-subtext text-muted-foreground sm:inline-block">
              ⌘K
            </kbd>
          ) : null}

          {copilotEnabled && (
            <Button
              type="button"
              size="icon-round-sm"
              variant={historyOpen ? "default" : "ghost"}
              aria-label="Chat history"
              title="Chat history"
              onClick={() => {
                setHistoryOpen((o) => !o);
                setQuery("");
                focusInput();
              }}
            >
              <Icon name="history" />
            </Button>
          )}

          {typing && !historyOpen && (
            <Button
              type="button"
              size="icon-round-sm"
              variant={route === "chat" ? "default" : "ghost"}
              aria-label={route === "chat" ? "Send" : "Search"}
              onClick={activateSel}
            >
              <Icon name={route === "chat" ? "arrow-up" : "search"} />
            </Button>
          )}
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── scope token (Slack-style scope switcher) ───────────────────────────────────

// The tag the dock prepends to the input to show the page you're searching, and
// the affordance to *change* it: click → a ranked dropdown (page scope first,
// then "Everything", then other indexed scopes). Reuses the DropdownMenu atom so
// the menu shares the app's item shape + elevation.
function ScopeChip({
  pageScope,
  activeScope,
  onPick,
  onOpenChange,
}: {
  pageScope: DockScope;
  activeScope: DockScope | null;
  onPick: (key: string | null) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const scoped = activeScope !== null;
  const label = activeScope?.label ?? "Everything";
  const icon = activeScope?.icon ?? "globe";
  const others = visibleScopes().filter((s) => s.key !== pageScope.key);
  const items: ContextMenuItem[] = [
    { type: "label", label: "Search in" },
    {
      label: pageScope.label,
      icon: <Icon name={pageScope.icon} className="size-3.5" />,
      shortcut: "This page",
      selected: activeScope?.key === pageScope.key,
      onSelect: () => onPick(pageScope.key),
    },
    {
      label: "Everything",
      icon: <Icon name="globe" className="size-3.5" />,
      selected: activeScope === null,
      onSelect: () => onPick(null),
    },
    ...others.map(
      (s): ContextMenuItem => ({
        label: s.label,
        icon: <Icon name={s.icon} className="size-3.5" />,
        selected: activeScope?.key === s.key,
        onSelect: () => onPick(s.key),
      }),
    ),
  ];
  return (
    <DropdownMenu items={items} align="start" side="top" widthPx={216} onOpenChange={onOpenChange}>
      {/* button-ignore: scope switcher token — opens the ranked scope dropdown (Slack-style), not a Button-atom action. */}
      <button
        type="button"
        aria-label={`Searching ${label} — change scope`}
        className={cn(
          "hidden shrink-0 items-center gap-1 rounded-full py-0.5 pl-2 pr-1.5 type-subtext font-medium transition-colors sm:inline-flex",
          scoped
            ? "bg-primary/10 text-primary hover:bg-primary/15"
            : "bg-muted text-muted-foreground hover:text-foreground",
        )}
      >
        <Icon name={icon} className="size-3" />
        {label}
        <Icon name="chevron-down" className="size-3 opacity-60" />
      </button>
    </DropdownMenu>
  );
}

// ─── results panel ─────────────────────────────────────────────────────────────

function ResultsPanel({
  rows,
  sel,
  onHover,
  typing,
}: {
  rows: FlatRow[];
  sel: number;
  onHover: (i: number) => void;
  typing: boolean;
}) {
  if (rows.length === 0) {
    return (
      <div className="px-4 py-6 text-center type-body text-muted-foreground">
        {typing ? "No matches." : "Type to search, or ask a question."}
      </div>
    );
  }
  let lastSection = "";
  return (
    <div className="max-h-[22rem] overflow-y-auto p-2">
      {rows.map((r, i) => {
        const header = r.section !== lastSection ? r.section : null;
        lastSection = r.section;
        return (
          <React.Fragment key={r.key}>
            {header && <PanelLabel className={i === 0 ? undefined : "mt-1"}>{header}</PanelLabel>}
            <DockRow
              icon={r.icon}
              title={r.title}
              subtitle={r.subtitle}
              shortcut={r.shortcut}
              accent={r.accent}
              active={i === sel}
              onMouseEnter={() => onHover(i)}
              onClick={r.activate}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── history panel ───────────────────────────────────────────────────────────────

function HistoryPanel({
  query,
  conversations,
  activeUid,
  sel,
  onHover,
  onNew,
  onSelect,
  onRename,
  onArchive,
}: {
  query: string;
  conversations: Array<{ uid: string; title: string | null; updated_at: string }>;
  activeUid: string | null;
  sel: number;
  onHover: (i: number) => void;
  onNew: () => void;
  onSelect: (uid: string) => void;
  onRename: (uid: string, title: string) => void;
  onArchive: (uid: string) => void;
}) {
  const searching = query.trim().length > 0;
  return (
    <div className="max-h-[24rem] overflow-y-auto p-2">
      {/* button-ignore: "New chat" command row in the history panel, not a Button-atom action. */}
      <button
        type="button"
        onMouseEnter={() => onHover(0)}
        onClick={onNew}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors",
          sel === 0 ? "bg-accent" : "hover:bg-accent/60",
        )}
      >
        <span className="grid size-7 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
          <Icon name="plus" className="size-3.5" />
        </span>
        <span className="flex-1 type-body text-foreground">New chat</span>
      </button>
      <PanelLabel className="mt-1">{searching ? "Matching chats" : "Recent chats"}</PanelLabel>
      {conversations.length === 0 ? (
        <div className="px-2 py-6 text-center type-body text-muted-foreground">
          {searching ? `No chats match “${query}”.` : "No chats yet — start one."}
        </div>
      ) : (
        conversations.map((c, i) => (
          <ConversationRow
            key={c.uid}
            uid={c.uid}
            title={c.title}
            updatedAt={c.updated_at}
            active={sel === i + 1 || c.uid === activeUid}
            onMouseEnter={() => onHover(i + 1)}
            onSelect={() => onSelect(c.uid)}
            onRename={onRename}
            onArchive={onArchive}
          />
        ))
      )}
    </div>
  );
}

function ConversationRow({
  uid,
  title,
  updatedAt,
  active,
  onMouseEnter,
  onSelect,
  onRename,
  onArchive,
}: {
  uid: string;
  title: string | null;
  updatedAt: string;
  active?: boolean;
  onMouseEnter?: () => void;
  onSelect: () => void;
  onRename: (uid: string, title: string) => void;
  onArchive: (uid: string) => void;
}) {
  const label = title?.trim() || "New chat";
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(label);
  React.useEffect(() => setDraft(label), [label]);

  function commit() {
    const t = draft.trim();
    if (t && t !== label) onRename(uid, t);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="px-2 py-1">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") {
              setDraft(label);
              setEditing(false);
            }
          }}
          onBlur={commit}
          aria-label="Rename chat"
        />
      </div>
    );
  }

  return (
    <div
      onMouseEnter={onMouseEnter}
      className={cn(
        "group/conv flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors",
        active ? "bg-accent" : "hover:bg-accent/60",
      )}
    >
      {/* button-ignore: chat-history conversation row body (opens the thread), not a Button-atom action. */}
      <button type="button" onClick={onSelect} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <span className="grid size-7 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
          <Icon name="message-circle" className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate type-body text-foreground">{label}</span>
      </button>
      <span className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover/conv:opacity-100">
        <Button type="button" size="icon-xs" variant="ghost" aria-label="Rename" onClick={() => setEditing(true)}>
          <Icon name="pencil" />
        </Button>
        <Button type="button" size="icon-xs" variant="ghost" aria-label="Archive" onClick={() => onArchive(uid)}>
          <Icon name="trash-2" />
        </Button>
      </span>
    </div>
  );
}

// ─── small parts ─────────────────────────────────────────────────────────────────

function PanelLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("px-2 py-1 type-subtext font-medium text-muted-foreground", className)}>{children}</div>
  );
}

function DockRow({
  icon,
  title,
  subtitle,
  shortcut,
  accent,
  active,
  onMouseEnter,
  onClick,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  shortcut?: string;
  accent?: boolean;
  active?: boolean;
  onMouseEnter?: () => void;
  onClick?: () => void;
}) {
  return (
    /* button-ignore: command-palette result row (icon + title + subtitle, keyboard-selectable), not a Button-atom action. */
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors",
        active ? "bg-accent" : "hover:bg-accent/60",
      )}
    >
      <span
        className={cn(
          "grid size-7 shrink-0 place-items-center rounded-md",
          accent ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        )}
      >
        <Icon name={icon} className="size-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate type-body text-foreground">{title}</span>
        {subtitle && <span className="block truncate type-subtext text-muted-foreground">{subtitle}</span>}
      </span>
      {active ? (
        <Icon name="corner-down-left" className="size-3 shrink-0 text-muted-foreground" />
      ) : shortcut ? (
        <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 type-subtext text-muted-foreground">
          {shortcut}
        </kbd>
      ) : null}
    </button>
  );
}

function PanelFooterHint() {
  return (
    <div className="flex items-center gap-3 border-t border-border/50 px-3 py-1.5 type-subtext text-muted-foreground/80">
      <span className="flex items-center gap-1">
        <kbd className="rounded border border-border bg-muted px-1 type-subtext">↑↓</kbd> navigate
      </span>
      <span className="flex items-center gap-1">
        <kbd className="rounded border border-border bg-muted px-1 type-subtext">↵</kbd> open
      </span>
      <span className="flex items-center gap-1">
        <kbd className="rounded border border-border bg-muted px-1 type-subtext">esc</kbd> close
      </span>
      <span className="ml-auto flex items-center gap-1">
        <kbd className="rounded border border-border bg-muted px-1 type-subtext">⌘K</kbd> toggle
      </span>
    </div>
  );
}
